import Vue from 'vue'
import { S, G, M, A } from './types'
import { Getters, Mutations, Actions } from 'vuex'
import { normalize } from 'normalizr'
import { scheduleSchema } from '@/schema/schedule'
import { ScheduleEntities } from '@/schema/schedule/types'
import { Schedule } from '@/types/schedule'
import store from '@/store'

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  schedules: {},
  sort: [],
  totalCount: 0,
  calendar: {
    filter: 'all',
    searchWord: '',
    targetDate: null,
    dateForCalendar: null
  }
})

/* -------------------------------------------------------------------------- */

const getters: Getters<S, G> = {
  schedules: state => state.schedules,
  sort: state => (Array.isArray(state.sort) ? state.sort : []),
  totalCount: state => state.totalCount,
  calendar: state => state.calendar
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  clear: state => {
    state.schedules = {}
    state.sort = []
    state.totalCount = 0
  },

  add: (
    state,
    { schedules, sort = undefined, totalCount = 0, toAppend = true }
  ) => {
    if (sort === undefined) {
      sort = Object.keys(schedules).map(id => Number(id))
    }

    state.sort = toAppend
      ? (state.sort || []).concat(sort)
      : sort.concat(state.sort || [])
    state.schedules = Object.assign({}, state.schedules, schedules)
    state.totalCount = totalCount
  },

  update: (state, { id, params }) => {
    Vue.set(state.schedules, id, Object.assign({}, state.schedules[id], params))
  },

  delete: (state, { id }) => {
    Vue.delete(state.schedules, id)
    if (Array.isArray(state.sort)) {
      const index = state.sort.indexOf(id)
      if (index > -1) {
        state.sort.splice(index, 1)
      }
    }
  },

  updateCalendar: (state, calendar) => {
    state.calendar = Object.assign({}, state.calendar, calendar)
  }
}

/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  async create({ commit }, { params }) {
    const response = await Vue.$api.schedule.create({ params })
    const { result, entities } = normalize<Schedule, ScheduleEntities, number>(
      response,
      scheduleSchema
    )
    commit('update', { id: result, params: entities.schedules[result] })
  },

  async replace({ commit }, { params, scheduleId }) {
    const response = await Vue.$api.schedule.replace({ params, scheduleId })
    const { result, entities } = normalize<Schedule, ScheduleEntities, number>(
      response,
      scheduleSchema
    )
    commit('update', { id: result, params: entities.schedules[result] })
  },

  async update({ commit }, { id, params }) {
    const response = await Vue.$api.schedule.update({ params, scheduleId: id })
    const { result, entities } = normalize<Schedule, ScheduleEntities, number>(
      response,
      scheduleSchema
    )
    commit('update', { id, params: entities.schedules[result] })
  },

  async load({ commit }, { scheduleId }) {
    const response = await Vue.$api.schedule.fetch({ scheduleId })
    const { entities } = normalize<Schedule, ScheduleEntities, number>(
      response,
      scheduleSchema
    )

    const { schedules, workers } = entities

    commit('add', { schedules })
    store.commit('worker/add', { workers })
  },

  async delete({ commit }, { scheduleId }) {
    await Vue.$api.schedule.delete({ scheduleId })
    commit('delete', { id: scheduleId })
  },

  updateCalendar: ({ commit }, calendar) => {
    commit('updateCalendar', calendar)
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
